/**
 * Field Label Resolver — Pure utility functions for resolving custom labels
 * on OpenSearch field options.
 *
 * Extracted from ProgramsPage. These functions resolve template custom field
 * labels, employee extra-field labels, and field province inference without
 * any component state dependencies.
 */
import { type FieldOption } from './opensearch-field-mapper';
import { stripTypeSuffixes } from './label-generation';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Try each regex pattern against `fieldName` and return the first capture
 * group match, or `null` if no pattern matches.
 */
export function extractMappedKey(fieldName: string, patterns: RegExp[]): string | null {
  for (const pattern of patterns) {
    const match = fieldName.match(pattern);
    if (match?.[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Extract the template-scoped key from a TAS/PSLF/SYF/SLR custom-field name.
 *
 * Handles both snake_case and camelCase variants, flattened and nested.
 */
export function extractTemplateLabelKey(fieldName: string): string | null {
  const patterns = [
    /tas_(?:program_instance|application|course)_custom_fields\.([^.]+)/,
    /tas(?:ProgramInstance|Application|Course)CustomFields\.([^.]+)/,
    /tas_(?:program_instance|application|course|program_template)_fields\.([^.]+)/,
    /tas(?:ProgramInstance|Application|Course|ProgramTemplate)Fields\.([^.]+)/,
    /tas_(?:program_instance|program_template)_fields_flattened\.([^.]+)/,
    /tas(?:ProgramInstance|ProgramTemplate)FieldsFlattened\.([^.]+)/,
    /tas_(?:program_instance|application|course|program_template)_custom_fields_flattened\.([^.]+)/,
    /tas(?:ProgramInstance|Application|Course|ProgramTemplate)CustomFieldsFlattened\.([^.]+)/,
    /(?:pslf|syf|slr)_[^.]*?_custom_fields(?:_flattened)?\.([^.]+)/,
    /(?:pslf|syf|slr)[A-Za-z0-9]*CustomFields(?:Flattened)?\.([^.]+)/,
  ];

  return extractMappedKey(fieldName, patterns);
}

/**
 * Extract the base employee extra-field source key from a field name.
 */
export function extractEmployeeSourceField(fieldName: string): string | null {
  const patterns = [
    /employee_ef_fields\.employee_([^._]+)(?:_[^.]+)?/,
    /all_employee_ef_fields\.employee_([^._]+)(?:_[^.]+)?/,
    /employee_fields\.employee_([^._]+)(?:_[^.]+)?/,
    /all_employee_fields\.employee_([^._]+)(?:_[^.]+)?/,
    /employeeEfFields\.employee_([^._]+)(?:[A-Z].+)?/,
    /allEmployeeEfFields\.employee_([^._]+)(?:[A-Z].+)?/,
    /employeeFields\.employee_([^._]+)(?:[A-Z].+)?/,
    /allEmployeeFields\.employee_([^._]+)(?:[A-Z].+)?/,
    /employee_custom_fields_flattened\.employee_([^._]+)(?:_[^.]+)?/,
    /employeeCustomFieldsFlattened\.employee_([^._]+)(?:[A-Z].+)?/,
    /employeeEfFields\.employee([A-Z][a-zA-Z0-9]+)/,
    /allEmployeeEfFields\.employee([A-Z][a-zA-Z0-9]+)/,
    /employeeFields\.employee([A-Z][a-zA-Z0-9]+)/,
    /allEmployeeFields\.employee([A-Z][a-zA-Z0-9]+)/,
    /employeeCustomFieldsFlattened\.employee([A-Z][a-zA-Z0-9]+)/,
  ];

  return extractMappedKey(fieldName, patterns);
}

// ---------------------------------------------------------------------------
// Key normalisation
// ---------------------------------------------------------------------------

/** Collapse to lowercase alphanumerics — used for fuzzy map lookups. */
export function normalizeLookupKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Convert a camelCase or dotted identifier to snake_case. */
export function toSnakeCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/\./g, '_')
    .toLowerCase();
}

// ---------------------------------------------------------------------------
// Mapped-value resolution
// ---------------------------------------------------------------------------

/**
 * Attempt to find a value in `map` for any of the `candidates`, trying
 * multiple normalisation strategies (exact, uppercase, lowercase,
 * snake_case, stripped type suffixes, fully-normalised).
 */
export function resolveMappedValue(
  map: Record<string, string>,
  candidates: Array<string | null | undefined>
): string | undefined {
  const keys = Object.keys(map);
  if (keys.length === 0) {
    return undefined;
  }

  const normalizedMap = new Map<string, string>();
  keys.forEach((key) => normalizedMap.set(normalizeLookupKey(key), map[key]!));

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const stripped = stripTypeSuffixes(candidate);
    const variants = [
      candidate,
      stripped,
      candidate.toUpperCase(),
      stripped.toUpperCase(),
      candidate.toLowerCase(),
      stripped.toLowerCase(),
      toSnakeCase(candidate),
      toSnakeCase(stripped),
    ];

    for (const variant of variants) {
      if (Object.hasOwn(map, variant)) {
        return map[variant];
      }

      const normalizedVariant = normalizeLookupKey(variant);
      if (normalizedMap.has(normalizedVariant)) {
        return normalizedMap.get(normalizedVariant);
      }
    }
  }

  return undefined;
}

// ---------------------------------------------------------------------------
// Field province inference
// ---------------------------------------------------------------------------

/**
 * Infer a TAS field's "province" (APPLICATION, COURSE, or PROGRAM) from
 * its key name. Returns `null` for fields that don't match a known pattern.
 */
export function inferFieldProvince(fieldName: string): string | null {
  const normalized = fieldName.toLowerCase();

  if (normalized.includes('tas_application') || normalized.includes('tasapplication')) {
    return 'APPLICATION';
  }

  if (normalized.includes('tas_course') || normalized.includes('tascourse')) {
    return 'COURSE';
  }

  if (
    normalized.includes('tas_program_instance') ||
    normalized.includes('tasprograminstance') ||
    normalized.includes('tas_program_template') ||
    normalized.includes('tasprogramtemplate')
  ) {
    return 'PROGRAM';
  }

  return null;
}

// ---------------------------------------------------------------------------
// Label application
// ---------------------------------------------------------------------------

/**
 * Apply template and company custom-field labels to a list of field options.
 *
 * Resolution order per field:
 *   1. API-provided label (from `apiLabelByFieldKey`)
 *   2. TAS template custom-field label (from `templateKeyLabelMap`)
 *      — only applied when the province matches
 *   3. Employee extra-field custom label (from `companyEfKeyLabelMap`)
 *   4. Original field label (fallthrough)
 */
export function applyTemplateAndCustomFieldLabels(
  fields: FieldOption[],
  sourceFieldByFieldKey: Record<string, string>,
  apiLabelByFieldKey: Record<string, string>,
  companyEfKeyLabelMap: Record<string, string>,
  templateKeyLabelMap: Record<string, string>,
  templateKeyProvinceMap: Record<string, string>
): FieldOption[] {
  return fields.map((field) => {
    const apiLabel = apiLabelByFieldKey[field.key];
    if (apiLabel) {
      return {
        ...field,
        label: apiLabel,
      };
    }

    const templateKey = extractTemplateLabelKey(field.key);
    const templateCustomLabel = templateKey
      ? resolveMappedValue(templateKeyLabelMap, [templateKey])
      : undefined;
    const templateProvince = templateKey
      ? resolveMappedValue(templateKeyProvinceMap, [templateKey])
      : undefined;
    const inferredProvince = inferFieldProvince(field.key);

    if (templateCustomLabel) {
      if (!templateProvince || !inferredProvince || templateProvince === inferredProvince) {
        return {
          ...field,
          label: templateCustomLabel,
        };
      }
    }

    const sourceField = sourceFieldByFieldKey[field.key] || extractEmployeeSourceField(field.key);
    const customLabel = sourceField
      ? resolveMappedValue(companyEfKeyLabelMap, [sourceField, field.key])
      : undefined;
    if (!customLabel) {
      return field;
    }

    return {
      ...field,
      label: customLabel,
    };
  });
}
